import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useCart } from '../../contexts/CartContext';
import { post } from '../../services/apiClient';

export default function CheckoutSuccessPage() {
  const { t } = useTranslation('landing');
  const [searchParams] = useSearchParams();
  const { clear } = useCart();
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState('');

  // PayPal returns ?token={orderID} after buyer approval
  const paypalToken = searchParams.get('token');
  // ECPay/Stripe return ?session_id or ?ref
  const sessionId = searchParams.get('session_id') || searchParams.get('ref');

  const orderRef = paypalToken || sessionId;

  useEffect(() => {
    // If PayPal token is present, capture the order
    if (paypalToken && !capturing) {
      setCapturing(true);
      post<{ orderId: string; status: string }>(`/api/v1/checkout/paypal/capture/${paypalToken}`, {})
        .then(() => {
          clear();
          setCapturing(false);
        })
        .catch((err) => {
          setError(err.message || 'Payment capture failed');
          setCapturing(false);
        });
    } else if (!paypalToken) {
      // ECPay/other — just clear the cart
      clear();
    }
  }, [paypalToken]);

  if (capturing) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-400">{t('checkout.processing', 'Processing your payment...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
        <div className="max-w-md mx-auto text-center px-6">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold mb-4">{t('checkout.error', 'Payment Failed')}</h1>
          <p className="text-gray-400 mb-8">{error}</p>
          <Link to="/cart" className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            {t('checkout.backToCart', 'Back to Cart')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
      <div className="max-w-md mx-auto text-center px-6">
        <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold mb-4">
          {t('checkout.successTitle', 'Thank you for your order!')}
        </h1>
        <p className="text-gray-400 mb-2">
          {t('checkout.successMessage', 'Your payment has been processed successfully. We will send a confirmation email shortly.')}
        </p>
        {orderRef && (
          <p className="text-gray-500 text-sm mb-8">
            {t('checkout.reference', 'Reference')}: {orderRef.length > 16 ? orderRef.slice(0, 16) + '...' : orderRef}
          </p>
        )}
        <Link
          to="/products/iot"
          className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {t('checkout.backToProducts', 'Back to Products')}
        </Link>
      </div>
    </div>
  );
}
